import { api } from './client'

/**
 * Catálogo de mensagens do sistema (spec 2026-07-25 §8).
 *
 * O Admin edita o *texto que o sistema diz* — não confundir com
 * `/notificacoes/preferencias`, que é o usuário escolhendo o que recebe.
 *
 * Dois escopos (spec 2026-07-27 §2.4), com o **mesmo** contrato de objeto:
 * - **plataforma** — `/v1/notification-templates`, gateado por
 *   `notificacoes:template_write` ou `admin:platform`;
 * - **organização** — `/v1/organizations/{org_id}/notification-templates`,
 *   gateado por `_can_manage_org` (gestor, responsável ou Admin). Aqui a lista
 *   traz só entradas `org_overridable` e o `source` diz de onde vem o texto
 *   vigente, porque a cascata é `org → plataforma → padrão`.
 */

/** Canais declarados no registry. `chat` existe por antecipação (sem entradas no v1). */
export type NotificationChannel = 'email' | 'feed' | 'chat'

/** Rótulo do canal em PT-BR (a tela e o drawer usam o mesmo texto). */
export const CHANNEL_LABEL: Record<string, string> = {
  email: 'E-mail',
  feed: 'Feed',
  chat: 'Chat',
}

export function channelLabel(channel: string): string {
  return CHANNEL_LABEL[channel] ?? channel
}

/**
 * Origem do texto vigente na cascata `org → plataforma → padrão`.
 *
 * Só faz sentido no escopo de organização: no escopo de plataforma o
 * `is_customized` já basta (há override da plataforma, ou é o padrão do Git).
 */
export type NotificationTemplateSource = 'org' | 'plataforma' | 'padrao'

/** Como explicar a origem do texto para quem edita. */
export const SOURCE_LABEL: Record<string, string> = {
  org: 'personalizado por esta Organização',
  plataforma: 'texto da plataforma (esta Organização não personalizou)',
  padrao: 'padrão do sistema',
}

export function sourceLabel(source: string | null | undefined): string | null {
  if (!source) return null
  return SOURCE_LABEL[source] ?? source
}

export interface NotificationTemplateVariable {
  name: string
  description: string
  required: boolean
}

/** Item do catálogo — registry + estado do override. Não traz o texto. */
export interface NotificationTemplateListItem {
  event_key: string
  channel: NotificationChannel
  /** Chave do domínio, usada para agrupar (ex.: `organizacao`). */
  domain: string
  /** Rótulo do domínio para exibir no cabeçalho do grupo. */
  domain_label: string
  label: string
  description: string
  /** Quem recebe — informativo, não editável. */
  recipients_note: string
  /** `false` nos itens ainda montados em código ("edição em breve"). */
  editable: boolean
  /** `true` quando existe override salvo (selo "personalizado"). */
  is_customized: boolean
  /**
   * `true` quando a entrada pode ter override por Organização (spec §2.2 — só
   * onde os destinatários são necessariamente membros daquela org).
   * Ausente nas respostas antigas do escopo de plataforma.
   */
  org_overridable?: boolean
  /** Presente no escopo de org: de onde vem o texto vigente. */
  source?: NotificationTemplateSource
  updated_by_email: string | null
  updated_at: string | null
}

/**
 * Override cujo `event_key` saiu do registry (spec 2026-07-27 §4).
 *
 * Sem esta lista a linha fica inalcançável: o catálogo deriva do registry, e o
 * `DELETE` de um par desconhecido responderia 404.
 */
export interface NotificationTemplateOrphan {
  event_key: string
  channel: string
  updated_by_email?: string | null
  updated_at?: string | null
}

/** Texto efetivo + padrão + variáveis de um par (event_key, channel). */
export interface NotificationTemplateDetail {
  event_key: string
  channel: NotificationChannel
  label: string
  description: string
  recipients_note: string
  editable: boolean
  /** E-mail: assunto. Feed: título do item. */
  subject: string | null
  /** E-mail: HTML interno (dentro do shell da marca). Feed: mensagem de uma linha. */
  body: string
  default_subject: string | null
  default_body: string
  is_customized: boolean
  /** Presente no escopo de org: de onde vem o texto vigente (rodapé do editor). */
  source?: NotificationTemplateSource
  variables: NotificationTemplateVariable[]
  /** Avisos de compatibilidade (não bloqueiam o salvar). */
  warnings: string[]
}

export interface NotificationTemplatePreview {
  subject: string | null
  /** HTML já sanitizado pelo servidor — renderizado em iframe sandbox, nunca injetado na SPA. */
  body_html: string
  text_body: string
  warnings: string[]
}

export interface NotificationTemplateInput {
  subject: string | null
  body: string
}

const BASE = '/v1/notification-templates'

/**
 * `orgId = null | undefined` → escopo de plataforma; número → escopo da org.
 *
 * Uma função por escopo duplicaria cinco chamadas idênticas; o escopo é só o
 * prefixo da rota, e o contrato do corpo é o mesmo nos dois.
 */
function base(orgId?: number | null): string {
  return orgId == null ? BASE : `/v1/organizations/${orgId}/notification-templates`
}

function path(eventKey: string, channel: string, orgId?: number | null): string {
  return `${base(orgId)}/${encodeURIComponent(eventKey)}/${encodeURIComponent(channel)}`
}

/**
 * Restrição de alçada do caller nesta Organização (spec 2026-07-29 §7).
 *
 * Presente só quando a capacidade `mensagens` foi retirada dele. É o que permite à
 * tela explicar o bloqueio em vez de exibir botões que só sabem responder 403.
 */
export interface NotificationTemplateRestriction {
  motivo: string
  desde: string
}

export const notificationTemplatesApi = {
  list: (orgId?: number | null) =>
    api.get<{ items: NotificationTemplateListItem[] }>(base(orgId)).then(r => r.data.items),

  /** Como o `list`, mas preservando a `restricao` que a rota por org devolve. */
  listWithScope: (orgId?: number | null) =>
    api.get<{
      items: NotificationTemplateListItem[]
      restricao?: NotificationTemplateRestriction | null
    }>(base(orgId)).then(r => ({
      items: r.data.items,
      restricao: r.data.restricao ?? null,
    })),

  get: (eventKey: string, channel: string, orgId?: number | null) =>
    api.get<NotificationTemplateDetail>(path(eventKey, channel, orgId)).then(r => r.data),

  save: (
    eventKey: string, channel: string, data: NotificationTemplateInput,
    orgId?: number | null,
  ) =>
    api.put<{ ok: boolean }>(path(eventKey, channel, orgId), data).then(r => r.data),

  /**
   * Apaga o override do escopo em que se está.
   *
   * Plataforma: o texto volta a acompanhar o Git. Organização: volta ao texto
   * da **plataforma** (e só na ausência dele, ao padrão) — é a cascata, e a
   * tela precisa dizer isso com essas palavras (spec §2.4).
   */
  reset: (eventKey: string, channel: string, orgId?: number | null) =>
    api.delete<{ ok: boolean }>(path(eventKey, channel, orgId)).then(r => r.data),

  /** Renderiza com o `sample_ctx` do registry — não persiste nada. */
  preview: (
    eventKey: string, channel: string, data: NotificationTemplateInput,
    orgId?: number | null,
  ) =>
    api
      .post<NotificationTemplatePreview>(`${path(eventKey, channel, orgId)}/preview`, data)
      .then(r => r.data),

  /** Overrides de eventos que saíram do catálogo (só escopo de plataforma). */
  orphans: () =>
    api.get<{ items: NotificationTemplateOrphan[] }>(`${BASE}/orphans`).then(r => r.data.items),

  /** Mesma rota do `reset` — nome próprio porque a intenção é limpeza, não restauração. */
  deleteOrphan: (eventKey: string, channel: string) =>
    api.delete<{ ok: boolean }>(path(eventKey, channel)).then(r => r.data),
}
